<template>
    <div class="range-slider">
        <div
            class="slider-tooltip"
            :style="{ left: tooltipPosition + '%' }"
            v-if="showTooltip"
        >
            {{ formattedValue }}
        </div>

        <div
            class="slider-track"
            ref="track"
            @mousedown="startDrag"
            @touchstart="startDrag"
        >
            <div
                class="slider-fill"
                :style="{ width: percentage + '%' }"
            ></div>

            <div
                class="slider-handle"
                :style="{ left: percentage + '%' }"
                @mousedown="startDrag"
                @touchstart="startDrag"
            ></div>
        </div>

        <div class="slider-ticks">
            <div
                v-for="(tick, index) in ticks"
                :key="index"
                class="tick"
                :style="{ left: tick.position + '%' }"
            >
                <div class="tick-mark"></div>
                <div class="tick-label">{{ tick.label }}</div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'plugin-home-possible-earnings-range-2',
    props: {
        value: {
            type: Number,
            default: 0,
        },
        min: {
            type: Number,
            default: 100000,
        },
        max: {
            type: Number,
            // default: 100000000,
            default: 60000000,
        },
        showTooltip: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            isDragging: false,
            currentValue: this.value || this.min,
            // currentValue: this.value,
        };
    },
    computed: {
        percentage() {
            const range = this.max - this.min;
            return ((this.currentValue - this.min) / range) * 100;
        },

        tooltipPosition() {
            return this.percentage;
        },

        formattedValue() {
            return this.formatValue(this.currentValue);
        },

        ticks() {
            return [
                { value: 100000, label: '100 тыс.', position: 0 },
                { value: 15000000, label: '15 млн.', position: 25 },
                { value: 30000000, label: '30 млн.', position: 50 },
                { value: 45000000, label: '45 млн.', position: 75 },
                { value: 60000000, label: '60 млн.', position: 100 },
            ];
        },
    },
    methods: {
        updateValue(e) {
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            this.currentValue = this.getValueFromPosition(clientX);
            this.$emit('input', this.currentValue);
        },
        getValueFromPosition(x) {
            const rect = this.$refs.track.getBoundingClientRect();
            const percentage = Math.max(0, Math.min(1, (x - rect.left) / rect.width));
            const range = this.max - this.min;
            const newValue = Math.round((percentage * range) + this.min);
            return Math.max(this.min, Math.min(this.max, newValue));
        },
        formatValue(val) {
            if (val >= 1000000) {
                const millions = val / 1000000;
                return millions % 1 === 0
                    ? `${millions} млн. руб.`
                    : `${millions.toFixed(1)} млн. руб.`;
            } else if (val >= 1000) {
                const thousands = Math.round(val / 1000);
                return `${thousands} тыс. руб.`;
            }
            return `${val} руб.`;
        },
        startDrag(e) {
            this.isDragging = true;
            this.updateValue(e);

            document.addEventListener('mousemove', this.onDrag);
            document.addEventListener('mouseup', this.stopDrag);
            document.addEventListener('touchmove', this.onDrag);
            document.addEventListener('touchend', this.stopDrag);

            e.preventDefault();
        },
        onDrag(e) {
            if (!this.isDragging) return;
            this.updateValue(e);
        },
        stopDrag() {
            this.isDragging = false;
            document.removeEventListener('mousemove', this.onDrag);
            document.removeEventListener('mouseup', this.stopDrag);
            document.removeEventListener('touchmove', this.onDrag);
            document.removeEventListener('touchend', this.stopDrag);
        },
    },
    watch: {
        value(newVal) {
            this.currentValue = newVal;
        },
    },
};
</script>

<style lang="less" scoped>
@import '~theme';

@white: #ffffff;
@black: #000000;

@slider-height: 4px;
@handle-size: 12px;
@transition-speed: 0.1s;

.flex-center() {
    display: flex;
    align-items: center;
    justify-content: center;
}

.range-slider {
    position: relative;
    padding-top: 40px;
    user-select: none;
    max-width: 800px;
    margin: 0 auto;

    .slider-tooltip {
        position: absolute;
        top: -8px;
        left: 0;
        transform: translateX(-50%);
        background: @color-primary;
        color: @white;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
        z-index: 10;

        &::after {
            content: '';
            position: absolute;
            bottom: -5px;
            left: 50%;
            transform: translateX(-50%);
            border-width: 6px 6px 0;
            border-style: solid;
            border-color: @color-primary transparent transparent transparent;
        }
    }

    .slider-track {
        position: relative;
        height: @slider-height;
        background: #E5DAF5;
        border-radius: 2px;
        cursor: pointer;
    }

    .slider-fill {
        position: absolute;
        height: 100%;
        background: @color-primary;
        border-radius: 2px;
        pointer-events: none;
    }

    .slider-handle {
        position: absolute;
        top: 50%;
        left: 0;
        transform: translate(-50%, -50%);
        width: @handle-size;
        height: @handle-size;
        background: @white;
        border: 1px solid @color-gray-main;
        border-radius: 50%;
        cursor: grab;
        // box-shadow: 0 2px 6px rgba(@black, 0.15);

        &:active {
            cursor: grabbing;
            transform: translate(-50%, -50%) scale(1.1);
        }
    }

    .slider-ticks {
        position: relative;
        height: 40px;
        margin-top: 15px;
    }

    .tick {
        position: absolute;
        left: 0;
        transform: translateX(-50%);
        text-align: center;
    }

    .tick-mark {
        width: 1px;
        height: 8px;
        background: @color-gray-main;
        margin: 0 auto 8px;
    }

    .tick-label {
        .typography-caption();

        font-size: 12px;
        color: @color-gray-main;
        white-space: nowrap;
    }
}
</style>
